import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ApiResponse, PaginatedApiResponse } from '../models/securepay';

export interface DisbursementActivationData {
  activationId: string;
  status: string;
  credentialsIssued: boolean;
}

export interface OwnersResponseData {
  businessApprovalStatus: string;
  approvalComment: string | null;
  owners: any[];
}

export interface NameInquiryPayload {
  accountNumber: string;
  channelCode: number;
  destinationInstitutionCode: string;
  stagingStatus?: boolean;
}

export interface NameInquiryData {
  accountName: string;
  accountNumber: string;
}

export interface ActivationFundingAccount {
  accountNumber: string;
  bankCode: string;
  bankName: string;
  amountThreshold: number;
  startDate: string;
  endDate: string;
  isDefault: boolean;
}

export interface CreateFundingSourcePayload {
  accountNumber: string;
  bankCode: string;
  bankName: string;
  perTransactionLimit: number;
  startDate: string;
  endDate: string;
  setAsDefault: boolean;
}

export interface FundingSourceActivationInstructions {
  bankName: string;
  accountName: string;
  accountNumber: string;
  amount: number;
}

export interface ActivationFundingSource {
  id: string;
  status: string;
  statusReason: string | null;
  isDefault: boolean;
  activationInstructions: FundingSourceActivationInstructions | null;
}

export interface FundingSourceRecord extends ActivationFundingSource {
  accountNumber: string;
  accountName: string;
  bankCode: string;
  bankName: string;
  amountThreshold: number;
  startDate: string | null;
  endDate: string | null;
}

export interface ActivationConfigurationData {
  fundingAccounts?: ActivationFundingSource[];
}

@Injectable({ providedIn: 'root' })
export class DisbursementActivationService {
  private http = inject(HttpClient);

  configure(baseUrl: string, activationId: string, payload: FormData) {
    return this.http.post<ApiResponse<ActivationConfigurationData | null>>(
      `${baseUrl}/api/Disbursement/activations/${encodeURIComponent(activationId)}/configuration`,
      payload,
    );
  }

  nameInquiry(baseUrl: string, payload: NameInquiryPayload) {
    return this.http.post<ApiResponse<NameInquiryData>>(
      `${baseUrl}/api/Disbursement/name-inquiry`,
      payload,
    );
  }

  getFundingSourceRecords(baseUrl: string, pageIndex = 1, pageSize = 7) {
    return this.http.get<ApiResponse<PaginatedApiResponse<FundingSourceRecord[]>>>(
      `${baseUrl}/api/v1/merchant-wallet/funding-sources/records`,
      { params: { pageIndex, pageSize } },
    );
  }

  createFundingSource(baseUrl: string, payload: CreateFundingSourcePayload) {
    return this.http.post<ApiResponse<ActivationFundingSource>>(
      `${baseUrl}/api/v1/merchant-wallet/funding-sources`,
      payload,
    );
  }

  confirmActivationFeeSent(baseUrl: string, fundingSourceId: string) {
    return this.http.post<ApiResponse<ActivationFundingSource>>(
      `${baseUrl}/api/v1/merchant-wallet/funding-sources/${encodeURIComponent(fundingSourceId)}/activation-fee-sent`,
      {},
    );
  }

  activate(baseUrl: string) {
    return this.http.post<ApiResponse<DisbursementActivationData>>(`${baseUrl}/api/Disbursement/activate`, {});
  }

  getOwners(baseUrl: string) {
    return this.http.get<ApiResponse<OwnersResponseData>>(`${baseUrl}/api/kyc/owners`);
  }
}
